import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, FormField } from '../components/ui';

function fmtTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  if (sameDay) return dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  const diffDays = Math.floor((now - dt) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return dt.toLocaleDateString('tr-TR', { weekday: 'short' });
  return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
}

// Balon altı: bugün → "HH:MM", aynı yıl → "29 Nis HH:MM", farklı yıl → "DD.MM.YYYY HH:MM"
function fmtMsgTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  const time = dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return time;
  if (dt.getFullYear() === now.getFullYear()) {
    return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) + ' ' + time;
  }
  return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + time;
}

// Gün ayrıştırıcı: "Bugün" / "Dün" / "29 Nisan 2026"
function fmtDayHeader(d) {
  const dt = new Date(d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yest  = new Date(today.getTime() - 86400000);
  const dtDay = new Date(dt); dtDay.setHours(0, 0, 0, 0);
  if (dtDay.getTime() === today.getTime()) return 'Bugün';
  if (dtDay.getTime() === yest.getTime())  return 'Dün';
  return dt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function sameDay(a, b) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export default function MessagesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [broadcast, setBroadcast] = useState({ unread_count: 0, last_at: null, last_body: null, last_sender_name: null });
  const [conversations, setConversations] = useState([]);
  const [recipients, setRecipients] = useState([]);

  const [selected, setSelected] = useState(null); // { type: 'broadcast' | 'peer', peerId?, name }
  const [thread, setThread] = useState([]);
  const [newBody, setNewBody] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // File
  const [pendingPreview, setPendingPreview] = useState(null); // object URL
  const [lightbox, setLightbox] = useState(null); // image URL büyütme
  const [search, setSearch] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatPeer, setNewChatPeer] = useState('');

  const messagesEndRef = useRef(null);
  const lastThreadCountRef = useRef(0);
  const textareaRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Resim seçilince önizleme oluştur, eski URL'yi serbest bırak
  useEffect(() => {
    if (!pendingImage) { setPendingPreview(null); return; }
    const url = URL.createObjectURL(pendingImage);
    setPendingPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImage]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Sadece resim dosyası yükleyebilirsiniz');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Dosya 10 MB\'dan büyük olamaz');
      return;
    }
    setPendingImage(file);
    e.target.value = ''; // aynı dosya tekrar seçilebilsin diye
  };

  // Mobil/desktop ayrımı — mobil'de sohbet açılınca tam ekran (modal)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Textarea auto-grow — mobilde maks 70px, desktop'ta 120px (gönder butonu daima görünür kalsın)
  const adjustTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const max = isMobile ? 70 : 120;
    ta.style.height = Math.min(ta.scrollHeight, max) + 'px';
  }, [isMobile]);

  useEffect(() => { adjustTextarea(); }, [newBody, adjustTextarea]);

  // Sohbet listesi yükle
  const fetchConvs = useCallback(async () => {
    try {
      const r = await api.get('/messages/conversations');
      if (r.data.success) {
        setBroadcast(r.data.data.broadcast || { unread_count: 0 });
        setConversations(r.data.data.conversations || []);
      }
    } catch (err) { /* sessiz */ }
  }, []);

  useEffect(() => {
    fetchConvs();
    api.get('/messages/recipients').then(r => {
      if (r.data.success) setRecipients(r.data.data || []);
    }).catch(() => {});
    // 5 sn'de bir conversation list refresh
    const t = setInterval(fetchConvs, 5000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchConvs(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchConvs]);

  // Çan'dan deep-link (localStorage)
  useEffect(() => {
    const openWith = localStorage.getItem('msgs_open_with');
    if (openWith) {
      localStorage.removeItem('msgs_open_with');
      if (openWith === 'broadcast') {
        setSelected({ type: 'broadcast', name: 'Herkes' });
      } else {
        setSelected({ type: 'peer', peerId: openWith, name: '' });
      }
    }
  }, []);

  // Seçili thread'in mesajlarını çek
  const fetchThread = useCallback(async () => {
    if (!selected) return;
    try {
      const target = selected.type === 'broadcast' ? 'broadcast' : selected.peerId;
      const r = await api.get('/messages/thread', { params: { with: target } });
      if (r.data.success) {
        setThread(r.data.data.items || []);
      }
    } catch (err) { /* sessiz */ }
  }, [selected]);

  // Thread aç → fetch + polling başlat (2 sn)
  useEffect(() => {
    if (!selected) { setThread([]); return; }
    fetchThread();
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      if (document.visibilityState === 'visible') fetchThread();
    };
    const t = setInterval(tick, 2000);
    return () => { stopped = true; clearInterval(t); };
  }, [selected, fetchThread]);

  // Yeni mesaj gelince en alta scroll
  useEffect(() => {
    if (thread.length !== lastThreadCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      lastThreadCountRef.current = thread.length;
    }
  }, [thread]);

  const sendMessage = async () => {
    if (!selected) return;
    if (!newBody.trim() && !pendingImage) return;
    setSending(true);
    try {
      let attachmentUrl = null;
      if (pendingImage) {
        const fd = new FormData();
        fd.append('image', pendingImage);
        const up = await api.post('/messages/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 dk — mobil bağlantı için
        });
        if (up.data.success) attachmentUrl = up.data.data.url;
      }
      await api.post('/messages', {
        recipient_id: selected.type === 'broadcast' ? null : selected.peerId,
        body: newBody.trim() || null,
        attachment_url: attachmentUrl,
      });
      setNewBody('');
      setPendingImage(null);
      await fetchThread();
      await fetchConvs();
    } catch (err) {
      alert(err.response?.data?.message || 'Gönderilemedi');
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    if (!newChatPeer) return;
    const r = recipients.find(x => x.id === newChatPeer);
    setSelected({ type: 'peer', peerId: newChatPeer, name: r?.full_name || r?.username || '' });
    setShowNewChat(false);
    setNewChatPeer('');
  };

  const handleDelete = async (mid) => {
    if (!window.confirm('Bu mesajı silmek istiyor musunuz?')) return;
    try {
      await api.delete('/messages/' + mid);
      await fetchThread();
      await fetchConvs();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  // Mevcut peer'ler (yeni sohbet listesinde gösterirken zaten sohbet ettikleri filtre)
  const filteredConvs = conversations.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.peer_name || '').toLowerCase().includes(s)
        || (c.peer_username || '').toLowerCase().includes(s)
        || (c.last_body || '').toLowerCase().includes(s);
  });

  // Sohbet listesinde seçilen target ile karşılaştırma
  const isSelectedConv = (c) => selected?.type === 'peer' && selected.peerId === c.peer_id;
  const isSelectedBroadcast = () => selected?.type === 'broadcast';

  // Header başlığı
  const headerTitle = selected
    ? (selected.type === 'broadcast'
        ? '📢 Herkes'
        : (conversations.find(c => c.peer_id === selected.peerId)?.peer_name
           || recipients.find(r => r.id === selected.peerId)?.full_name
           || selected.name || 'Kullanıcı'))
    : null;

  return (
    <div style={{ display: 'flex', height: isMobile ? 'calc(100dvh - 56px)' : 'calc(100vh - 120px)', minHeight: 400, position: 'relative' }}>
      {/* SOL: Konuşma listesi — mobil'de bir sohbet seçilince gizlenir */}
      <div style={{
        width: isMobile ? '100%' : 320,
        borderRight: isMobile ? 'none' : '1px solid var(--border-secondary)',
        background: 'var(--bg-card)',
        flexDirection: 'column',
        display: (isMobile && selected) ? 'none' : 'flex',
      }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Mesajlar</h2>
            <Button size="sm" onClick={() => setShowNewChat(true)}>+ Yeni</Button>
          </div>
          <input
            type="text"
            placeholder="Sohbet ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Broadcast (Herkes) — en üstte sabit */}
          <ConversationItem
            active={isSelectedBroadcast()}
            onClick={() => setSelected({ type: 'broadcast', name: 'Herkes' })}
            avatar="📢"
            title="Herkes"
            subtitle={(() => {
              const prefix = broadcast.last_sender_name ? broadcast.last_sender_name + ': ' : '';
              if (broadcast.last_body) return prefix + broadcast.last_body;
              if (broadcast.last_attachment_url) return prefix + '📷 Resim';
              return 'Henüz mesaj yok';
            })()}
            time={broadcast.last_at}
            unread={broadcast.unread_count}
            badge="Herkese"
          />

          <div style={{ height: 1, background: 'var(--border-secondary)', margin: '4px 0' }}></div>

          {filteredConvs.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
              Henüz sohbet yok.<br />
              <Button variant="ghost" size="sm" onClick={() => setShowNewChat(true)}>+ İlk sohbeti başlat</Button>
            </div>
          )}
          {filteredConvs.map(c => {
            const initials = (c.peer_name || c.peer_username || '?').split(' ').map(x => x[0]).join('').toUpperCase().slice(0, 2);
            const lastFromMe = c.last_sender_id === user?.id;
            return (
              <ConversationItem
                key={c.peer_id}
                active={isSelectedConv(c)}
                onClick={() => setSelected({ type: 'peer', peerId: c.peer_id, name: c.peer_name })}
                avatar={initials}
                title={c.peer_name || c.peer_username}
                subtitle={(lastFromMe ? 'Sen: ' : '') + (c.last_body || (c.last_attachment_url ? '📷 Resim' : ''))}
                time={c.last_at}
                unread={c.unread_count}
              />
            );
          })}
        </div>
      </div>

      {/* SAĞ: Sohbet penceresi — mobil'de seçim olunca tam ekran modal davranır */}
      <div style={{
        flex: 1,
        flexDirection: 'column',
        background: 'var(--bg-primary)',
        display: (isMobile && !selected) ? 'none' : 'flex',
        ...(isMobile && selected ? { position: 'absolute', inset: 0, zIndex: 30 } : {}),
      }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>💬</div>
            Soldan bir sohbet seçin veya yeni başlatın.
          </div>
        ) : (
          <>
            {/* Sohbet başlığı */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-card)' }}>
              {isMobile && (
                <button
                  onClick={() => setSelected(null)}
                  title="Sohbet listesi"
                  style={{
                    background: 'transparent', border: 'none',
                    color: 'var(--text-primary)', cursor: 'pointer',
                    padding: 4, display: 'flex', alignItems: 'center',
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{headerTitle}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  {selected.type === 'broadcast' ? 'Tüm kullanıcılar görür' : 'Direkt mesaj'}
                </div>
              </div>
            </div>

            {/* Mesaj balonları */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {thread.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.82rem', marginTop: 40 }}>
                  Henüz mesaj yok. İlk mesajı yazın.
                </div>
              )}
              {thread.map((m, idx) => {
                const isOwn = m.sender_id === user?.id;
                const isBroadcastMsg = !m.recipient_id;
                const prev = idx > 0 ? thread[idx - 1] : null;
                const showDayHeader = !prev || !sameDay(prev.created_at, m.created_at);
                return (
                  <React.Fragment key={m.id}>
                    {showDayHeader && (
                      <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0 4px' }}>
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 600,
                          padding: '4px 12px', borderRadius: 12,
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border-secondary)',
                          color: 'var(--text-secondary)',
                        }}>{fmtDayHeader(m.created_at)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        maxWidth: '70%',
                        padding: '8px 12px',
                        borderRadius: 12,
                        background: isOwn ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-card)',
                        color: isOwn ? '#fff' : 'var(--text-primary)',
                        border: isOwn ? 'none' : '1px solid var(--border-secondary)',
                        position: 'relative',
                      }}>
                        {/* Broadcast thread'inde gönderen adı */}
                        {(selected.type === 'broadcast' && !isOwn) && (
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--accent-primary)', marginBottom: 2 }}>
                            {m.sender_name || m.sender_username}
                          </div>
                        )}
                        {m.attachment_url && (
                          <img
                            src={m.attachment_url}
                            alt="Resim"
                            onClick={() => setLightbox(m.attachment_url)}
                            style={{
                              display: 'block',
                              maxWidth: '100%', maxHeight: 320,
                              borderRadius: 8, cursor: 'zoom-in',
                              marginBottom: m.body ? 6 : 2,
                            }}
                          />
                        )}
                        {m.body && <div style={{ fontSize: '0.88rem', lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', marginTop: 4, fontSize: '0.66rem', opacity: 0.75 }}>
                          {isBroadcastMsg && (
                            <span style={{ fontWeight: 600, padding: '1px 5px', borderRadius: 4, background: isOwn ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.08)' }}>
                              Herkese
                            </span>
                          )}
                          <span title={new Date(m.created_at).toLocaleString('tr-TR')}>{fmtMsgTime(m.created_at)}</span>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(m.id)}
                              title="Sil (admin)"
                              style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, opacity: 0.6, fontSize: '0.85rem' }}
                            >🗑</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef}></div>
            </div>

            {/* Resim önizleme (gönder öncesi) */}
            {pendingPreview && (
              <div style={{
                padding: 8, borderTop: '1px solid var(--border-secondary)',
                background: 'var(--bg-card)',
                display: 'flex', alignItems: 'center', gap: 10,
                flexShrink: 0,
              }}>
                <img src={pendingPreview} alt="önizleme"
                     style={{ height: 64, borderRadius: 6, border: '1px solid var(--border-secondary)' }} />
                <div style={{ flex: 1, fontSize: '0.78rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pendingImage?.name} · {(pendingImage?.size / 1024).toFixed(0)} KB
                </div>
                <button
                  onClick={() => setPendingImage(null)}
                  title="Resmi kaldır"
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            )}

            {/* Mesaj yaz — flex-shrink:0, gönder butonu daima görünür */}
            <div style={{
              padding: 10, borderTop: '1px solid var(--border-secondary)',
              background: 'var(--bg-card)',
              display: 'flex', gap: 8, alignItems: 'flex-end',
              flexShrink: 0,
            }}>
              {/* Hidden file inputs */}
              <input ref={galleryInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />
              <input ref={cameraInputRef}  type="file" accept="image/*" capture="environment" hidden onChange={handleFileSelect} />

              {/* Galeri / dosya */}
              <button
                onClick={() => galleryInputRef.current?.click()}
                title="Galeri / dosya"
                style={{
                  flexShrink: 0, width: 38, height: 38, borderRadius: '50%',
                  border: '1px solid var(--border-secondary)',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>

              {/* Kamera */}
              <button
                onClick={() => cameraInputRef.current?.click()}
                title="Kamera"
                style={{
                  flexShrink: 0, width: 38, height: 38, borderRadius: '50%',
                  border: '1px solid var(--border-secondary)',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>

              <textarea
                ref={textareaRef}
                rows={1}
                value={newBody}
                onChange={e => setNewBody(e.target.value)}
                onKeyDown={onKey}
                placeholder={selected.type === 'broadcast'
                  ? 'Tüm kullanıcılara mesaj...'
                  : (isMobile ? 'Mesaj yaz...' : 'Mesaj yaz... (Enter = gönder, Shift+Enter = yeni satır)')}
                style={{
                  flex: 1, resize: 'none',
                  padding: '8px 12px', borderRadius: 18,
                  border: '1px solid var(--border-secondary)',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  fontSize: '16px', fontFamily: 'inherit',  // 16px → iOS auto-zoom önler
                  lineHeight: 1.3,
                  minHeight: 38, maxHeight: isMobile ? 70 : 120,
                  overflowY: 'auto',
                }}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !newBody.trim()}
                title="Gönder"
                style={{
                  flexShrink: 0,
                  width: 42, height: 42,
                  borderRadius: '50%',
                  border: 'none',
                  background: (sending || !newBody.trim()) ? 'var(--bg-secondary)' : 'var(--accent-primary, #3b82f6)',
                  color: '#fff',
                  cursor: (sending || !newBody.trim()) ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: (sending || !newBody.trim()) ? 0.5 : 1,
                  transition: 'background 0.15s',
                }}
              >
                {sending
                  ? <span style={{ fontSize: '0.7rem' }}>...</span>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
                }
              </button>
            </div>
          </>
        )}
      </div>

      {/* Resim lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <img src={lightbox} alt="" style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain' }} />
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            style={{
              position: 'absolute', top: 12, right: 12,
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(0,0,0,0.6)', color: '#fff',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="Kapat"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* Yeni sohbet modal */}
      {showNewChat && (
        <Modal
          title="Yeni Sohbet"
          size="sm"
          onClose={() => setShowNewChat(false)}
          cancelAction={{ label: 'Vazgeç', onClick: () => setShowNewChat(false) }}
          primaryAction={{ label: 'Sohbet Başlat', onClick: startNewChat, disabled: !newChatPeer }}
        >
          <FormField label="Kullanıcı seçin" fullWidth>
            <select value={newChatPeer} onChange={e => setNewChatPeer(e.target.value)}>
              <option value="">— Kullanıcı seçin —</option>
              {recipients.map(r => (
                <option key={r.id} value={r.id}>
                  {r.full_name || r.username} {r.email ? `(${r.email})` : ''}
                </option>
              ))}
            </select>
          </FormField>
        </Modal>
      )}
    </div>
  );
}

function ConversationItem({ active, onClick, avatar, title, subtitle, time, unread, badge }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        cursor: 'pointer',
        background: active ? 'var(--bg-secondary)' : 'transparent',
        borderLeft: active ? '3px solid var(--accent-primary, #3b82f6)' : '3px solid transparent',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-hover, var(--bg-secondary))'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{
        width: 38, height: 38,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-secondary)',
        fontSize: '1rem', fontWeight: 600,
        flexShrink: 0,
      }}>
        {avatar}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontWeight: unread > 0 ? 700 : 600, fontSize: '0.86rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
            {badge && <span style={{ marginLeft: 6, fontSize: '0.66rem', padding: '1px 6px', borderRadius: 8, background: 'rgba(99,102,241,0.15)', color: 'var(--accent-primary, #6366f1)' }}>{badge}</span>}
          </span>
          {time && <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtTime(time)}</span>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{
            fontSize: '0.78rem',
            color: 'var(--text-secondary)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            flex: 1,
            fontWeight: unread > 0 ? 600 : 400,
          }}>
            {subtitle}
          </span>
          {unread > 0 && (
            <span style={{
              minWidth: 18, height: 18, padding: '0 5px',
              background: '#dc2626', color: '#fff',
              borderRadius: 9, fontSize: '0.66rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>{unread > 99 ? '99+' : unread}</span>
          )}
        </div>
      </div>
    </div>
  );
}
